<template>
  <SDialog
    :key="key"
    :show-dialog="showDialog"
    :width="width"
    :title="title"
    :card-max-height="maxHeight"
    :text="text"
    :cancel-text="cancelText"
    :text-class="textClass"
    v-bind="$attrs"
    @close="close"
    @update:show-dialog="onShowDialogChange"
    @keydown.enter="onEnter"
  >
    <SLoadingOverlay :loading="loading" />
    <slot v-if="$slots.default" />
    <VCardActions>
      <VContainer class="py-0 v2-padding">
        <VRow no-gutters>
          <VCol cols="12">
            <SBtn
              ref="defaultAction"
              :disabled="loading"
              action
              color="primary"
              data-testid="confirm-dialog-btn-confirm"
              block
              @click="confirmAction"
            >
              {{ confirmText || $t('input.btn.delete') }}
            </SBtn>
          </VCol>
        </VRow>
        <slot v-if="$slots['additional-actions']" name="additional-actions" />
      </VContainer>
    </VCardActions>
  </SDialog>
</template>

<script  setup lang="ts">

interface Props {
 text?: string
 title?: string
 confirmText?: string
 cancelText?: string
 maxHeight?: string
 textClass?: string
 actionFunction?: () => void
 width?: number
 showDialog?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  width: 325,
  showDialog: true,
  text: undefined,
  title: undefined,
  maxHeight: undefined,
  confirmText: undefined,
  cancelText: undefined,
  textClass: undefined,
  actionFunction: undefined
})

const emits = defineEmits<{
  (e: 'close'): void
  (e: 'update:show-dialog', value: boolean): void
}>()

const key = ref(0)
const defaultAction = useTemplateRef('defaultAction')

// Set once the owner has been told the dialog is hidden, so a dismissal reports
// it once. SDialog's X, Cancel and click-outside emit update:showDialog and
// then close, and each would otherwise tell the owner: an owner running
// cancel on @update:show-dialog (CountryForm) ran it twice.
let hidden = false
const hide = () => {
  if (hidden) return
  hidden = true
  emits('update:show-dialog', false)
}

const close = () => {
  emits('close')
  hide()
}

// SDialog closes itself on Escape (VOverlay flips its model) and reports only
// update:showDialog. Without passing that on, the owner's v-model stayed true:
// the dialog was gone, and setting true again changed nothing, so it could not
// be reopened -- every table's delete confirmation after one Escape. Only the
// model is updated, not `close`: owners treat close as more than hiding (see
// SDialog's emitCloseOnEscape).
const onShowDialogChange = (open: boolean) => {
  if (!open && props.showDialog) hide()
}

const { processAction, loading } = useAction()
// processAction sets `loading` before its first await, so this ignores a double click
// or a repeated Enter from a slot text field while the action runs. `loading` also
// disables Confirm.
const confirmAction = async () => {
  if (loading.value) return
  await processAction(async () => {
    if (props.actionFunction) {
      await props.actionFunction()
    }
    close()
  })
  // A failed action leaves the dialog open. Disabling the focused Confirm button can
  // drop focus to the body, where Enter no longer reaches this dialog, so put it back.
  if (!hidden) {
    await nextTick()
    if (!document.activeElement || document.activeElement === document.body) {
      defaultAction.value?.$el?.focus()
    }
  }
}

// Controls that handle Enter themselves. A button or link turns it into a click
// (Cancel and X dismiss, Confirm confirms), a textarea takes a new line, and a
// checkbox, radio, switch, select or combobox/listbox keeps Enter for its own
// toggle or menu. Enter on any of them must not also confirm the dialog.
const OWNS_ENTER = [
  'button', 'a', 'textarea', 'select', '[contenteditable]:not([contenteditable="false"])',
  'input[type="checkbox"]', 'input[type="radio"]',
  '[role="button"]', '[role="checkbox"]', '[role="radio"]', '[role="switch"]',
  '[role="combobox"]', '[role="listbox"]', '[role="option"]',
].join(', ')

// The Enter listener sits on the overlay root, so it hears Enter from every control
// in the dialog. Only Enter from a control that does not own it (a text field in the
// slot) confirms here.
const onEnter = (event: KeyboardEvent) => {
  const target = event.target
  if (target instanceof Element && target.closest(OWNS_ENTER)) return
  confirmAction()
}

watch(() => props.showDialog, async (showDialog) => {
  if (showDialog) {
    hidden = false
    key.value = Date.now()
    await nextTick()
    defaultAction.value?.$el?.focus()
  }
})
</script>

<style lang="scss" scoped>
</style>
